import { useRef, useEffect, useState } from 'react';
import { FileText, Printer, Save, ChevronDown, Trash2, FolderOpen, Loader2 } from 'lucide-react';
import { ReportPage } from './ReportPage';

// Inject global print styles once
const PRINT_STYLE_ID = 'report-print-styles';
function ensurePrintStyles() {
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    @media print {
      @page { size: A4; margin: 0; }
      body > div > div > div:first-child { display: none !important; }
      .report-chat-sidebar { display: none !important; }
      .report-toolbar { display: none !important; }
      .report-workspace { padding: 0 !important; background: white !important; overflow: visible !important; }
      .report-page-wrapper {
        margin: 0 !important; box-shadow: none !important; border: none !important;
        border-radius: 0 !important;
        page-break-after: always; break-after: page;
      }
      .report-page-wrapper:last-child { page-break-after: auto; break-after: auto; }
      .report-page, .report-page-content {
        min-height: 297mm !important;
        page-break-inside: avoid; break-inside: avoid;
      }
    }
  `;
  document.head.appendChild(style);
}

export function ReportWorkspace({
  reportTitle, pages, onPageChange,
  onSave, saving, activeReportId,
  savedReports, onLoadReport, onDeleteReport,
}) {
  useEffect(() => { ensurePrintStyles(); }, []);
  const workspaceRef = useRef(null);
  const [showSavedReports, setShowSavedReports] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowSavedReports(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handlePrint = () => {
    const echartsLib = window.echarts;
    if (!echartsLib) {
      window.print();
      return;
    }

    // Convert all ECharts canvases to static images before printing
    const chartDivs = workspaceRef.current?.querySelectorAll('[data-echarts]') || [];
    const snapshots = [];

    chartDivs.forEach(div => {
      const chart = echartsLib.getInstanceByDom(div);
      if (chart) {
        snapshots.push({ div, html: div.innerHTML });
        try {
          const dataUrl = chart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
          const img = document.createElement('img');
          img.src = dataUrl;
          img.style.width = '100%';
          img.style.height = div.style.height || '300px';
          img.style.display = 'block';
          div.innerHTML = '';
          div.appendChild(img);
        } catch (e) {
          console.warn('Failed to snapshot chart:', e);
        }
      }
    });

    // Print after a brief delay so images render
    setTimeout(() => {
      window.print();

      // Restore chart canvases after print dialog closes
      setTimeout(() => {
        snapshots.forEach(({ div, html }) => {
          div.innerHTML = html;
          try {
            const option = JSON.parse(div.getAttribute('data-echarts'));
            const chart = echartsLib.init(div, null, { renderer: 'canvas' });
            chart.setOption(option);
          } catch {}
        });
      }, 500);
    }, 100);
  };

  if (!pages || pages.length === 0) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden report-workspace">
        {/* Toolbar with load button even when empty */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-200 bg-white report-toolbar">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Financial Report</h2>
            <p className="text-xs text-zinc-500">No pages yet</p>
          </div>
          {savedReports?.length > 0 && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowSavedReports(!showSavedReports)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors shadow-sm"
              >
                <FolderOpen className="h-3.5 w-3.5" />
                Load Report
                <ChevronDown className="h-3 w-3" />
              </button>
              {showSavedReports && (
                <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-zinc-200 rounded-lg shadow-lg z-20 py-1 max-h-64 overflow-y-auto">
                  {savedReports.map(r => (
                    <div key={r.id} className="flex items-center justify-between px-3 py-2 hover:bg-zinc-50 group">
                      <button
                        onClick={() => { onLoadReport(r.id); setShowSavedReports(false); }}
                        className="flex-1 text-left"
                      >
                        <div className="text-xs font-medium text-zinc-800 truncate">{r.title}</div>
                        <div className="text-[10px] text-zinc-400">
                          {r.company_names?.join(', ') || 'No companies'}
                          {r.updated_at && ` · ${new Date(r.updated_at).toLocaleDateString()}`}
                        </div>
                      </button>
                      {onDeleteReport && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onDeleteReport(r.id); }}
                          className="opacity-0 group-hover:opacity-100 p-1 text-zinc-400 hover:text-red-500 transition-all"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-zinc-400 px-8 bg-zinc-50">
          <FileText className="h-16 w-16 mb-4 opacity-30" />
          <h2 className="text-xl font-light text-zinc-400 mb-2">No report generated yet</h2>
          <p className="text-sm text-zinc-500 text-center max-w-md">
            Use the chat to generate a financial summary. Try: "Create an executive summary for 2025"
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden report-workspace">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-200 bg-white report-toolbar">
        <div>
          <h2 className="text-sm font-semibold text-zinc-900">{reportTitle || 'Financial Report'}</h2>
          <p className="text-xs text-zinc-500">
            {pages.length} page{pages.length > 1 ? 's' : ''}
            {activeReportId && <span className="text-zinc-400"> · Saved</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Load saved reports */}
          {savedReports?.length > 0 && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowSavedReports(!showSavedReports)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors shadow-sm"
              >
                <FolderOpen className="h-3.5 w-3.5" />
                Load
                <ChevronDown className="h-3 w-3" />
              </button>
              {showSavedReports && (
                <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-zinc-200 rounded-lg shadow-lg z-20 py-1 max-h-64 overflow-y-auto">
                  {savedReports.map(r => (
                    <div key={r.id} className="flex items-center justify-between px-3 py-2 hover:bg-zinc-50 group">
                      <button
                        onClick={() => { onLoadReport(r.id); setShowSavedReports(false); }}
                        className="flex-1 text-left"
                      >
                        <div className="text-xs font-medium text-zinc-800 truncate">{r.title}</div>
                        <div className="text-[10px] text-zinc-400">
                          {r.company_names?.join(', ') || 'No companies'}
                          {r.updated_at && ` · ${new Date(r.updated_at).toLocaleDateString()}`}
                        </div>
                      </button>
                      {onDeleteReport && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onDeleteReport(r.id); }}
                          className="opacity-0 group-hover:opacity-100 p-1 text-zinc-400 hover:text-red-500 transition-all"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Save */}
          {onSave && (
            <button
              onClick={onSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors shadow-sm disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {activeReportId ? 'Update' : 'Save'}
            </button>
          )}

          {/* Print */}
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors shadow-sm"
          >
            <Printer className="h-3.5 w-3.5" />
            Print / PDF
          </button>
        </div>
      </div>

      {/* Pages */}
      <div ref={workspaceRef} className="flex-1 overflow-y-auto px-6 py-8 bg-zinc-100 print:bg-white print:p-0">
        {pages
          .filter(p => p && p.html)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((page, i) => (
            <ReportPage
              key={`${page.order}-${i}`}
              html={page.html}
              pageNumber={page.order || i + 1}
              totalPages={pages.length}
              onContentChange={(newHtml) => {
                if (onPageChange) {
                  const updated = [...pages];
                  updated[i] = { ...updated[i], html: newHtml };
                  onPageChange(updated);
                }
              }}
            />
          ))}
      </div>
    </div>
  );
}
